import { useCallback } from 'react';
import { portalAxios } from '@/lib/axios';
import { useSession } from '@/hooks/useSession';
import { processMenuData } from '@/lib/menu-utils';
import type { ProcessedMenuGroup } from '@/utils/types';

interface GlobalMenuCache {
  data: ProcessedMenuGroup[] | null;
  tokenKey: string | null;
}

declare global {
  interface Window {
    __PORTAL_MENU_CACHE__?: GlobalMenuCache;
  }
}

const generateTokenKey = (token: string): string => {
  const start = token.substring(0, 5);
  const middle = token.substring(
    Math.floor(token.length / 2) - 2,
    Math.floor(token.length / 2) + 3
  );
  const end = token.substring(token.length - 5);

  return `${start}${middle}${end}`;
};

const getGlobalMenuCache = (): GlobalMenuCache => {
  if (!window.__PORTAL_MENU_CACHE__) {
    window.__PORTAL_MENU_CACHE__ = {
      data: null,
      tokenKey: null,
    };
  }
  return window.__PORTAL_MENU_CACHE__;
};

const setGlobalMenuCache = (
  data: ProcessedMenuGroup[],
  tokenKey: string
): void => {
  window.__PORTAL_MENU_CACHE__ = {
    data,
    tokenKey,
  };
};

export const clearGlobalMenuCache = (): void => {
  if (window.__PORTAL_MENU_CACHE__) {
    window.__PORTAL_MENU_CACHE__ = {
      data: null,
      tokenKey: null,
    };
  }
};

interface UsePortalMenuReturn {
  getPortalMenu: () => Promise<ProcessedMenuGroup[]>;
}

export function usePortalMenu(): UsePortalMenuReturn {
  const { sessionData } = useSession();

  const getPortalMenu = useCallback(async (): Promise<ProcessedMenuGroup[]> => {
    if (!sessionData?.access_token) {
      return [];
    }

    const globalCache = getGlobalMenuCache();
    const currentTokenKey = generateTokenKey(sessionData.access_token);

    if (globalCache.data && globalCache.tokenKey === currentTokenKey) {
      return globalCache.data;
    }

    const response = await portalAxios.get('/nsportal/apps/user', {
      headers: {
        Authorization: `Bearer ${sessionData?.access_token}`,
      },
    });

    const processedData = await processMenuData(response.data);

    setGlobalMenuCache(processedData, currentTokenKey);

    return processedData;
  }, [sessionData?.access_token]);

  return {
    getPortalMenu,
  };
}
